import React, { useState, useEffect } from "react";
import {
    ThemeProvider,
    Typography,
    Grid,
    Box,
    Card,
    CardHeader,
    CardContent,
    Breadcrumbs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    TextField,
    Button,
    Paper
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { styled } from "@mui/material/styles";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { createTheme } from "@mui/material/styles";
import { getTechnicians } from "../API/technicianAPI";
import { getJobOrders } from "../API/jobOrderAPI";
import { Bar, Pie } from 'react-chartjs-2';
import 'chart.js/auto';
import BaseWrapper from "../../components/shared/BaseWrapper";

const theme = createTheme({
    palette: {
        primary: {
            main: "#1e5d45", // Custom primary color
        },
    },
});

const StyledHr = styled("hr")(({ theme }) => ({
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    // margin: theme.spacing(4, 0),
    opacity: 0.8,
}));

const formatTime = (time) => {
    if (!time) return "N/A";  // Handle cases where time might be missing

    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const period = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12; // Convert 0 to 12 for midnight

    return `${formattedHour}:${minutes} ${period}`;
};


function ViewOpenJobOrders() {

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false); // State to manage loading
    const [pastDueJobOrders, setPastDueJobOrders] = useState([])
    const [upcomingJobOrders, setUpcomingJobOrders] = useState([])
    const [openJobOrdersCount, setOpenJobOrdersCount] = useState([])


    useEffect(() => {
        const fetchJobOrders = async () => {
            setLoading(true);
            try {
                // Fetch job orders with the corrected clientId
                const results = await getJobOrders(
                    ['Not Done'],
                    [],
                    [],
                    null,
                    null,
                    null
                );

                const transformedResults = results.map((job) => {
                    const jobScope =
                        job.competencies
                            ?.filter(
                                (competency) =>
                                    competency !== "driving_2_wheels" &&
                                    competency !== "driving_4_wheels"
                            )
                            .map((competency) => {
                                const formattedCompetency = competency
                                    .replace(/_/g, " ") // Replace underscores with spaces
                                    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word

                                const specializationKey =
                                    competency === "general_pest_control"
                                        ? "general_pest_control"
                                        : "termite";
                                const formattedSpecialization = (
                                    job.specialization?.[specializationKey] || []
                                ).map((word) => {
                                    // Replace underscores with spaces
                                    const replacedWord = word.replace(/_/g, " ");

                                    // Format words based on length and position
                                    return replacedWord
                                        .split(" ")
                                        .map(
                                            (w, index) =>
                                                w.length === 2
                                                    ? w.toUpperCase() // Capitalize the entire word if its length is 2
                                                    : w.charAt(0).toUpperCase() + w.slice(1) // Capitalize the first letter of each word
                                        )
                                        .join(" "); // Join back into a single string
                                });

                                return {
                                    competency: formattedCompetency,
                                    specialization: formattedSpecialization,
                                };
                            }) || [];

                    const formatTime = (time) => {
                        if (!time) return "";
                        const [hour, minute] = time.split(":").map(Number);
                        const period = hour >= 12 ? "PM" : "AM";
                        const formattedHour = hour % 12 || 12; // Convert 0 to 12 for AM/PM format
                        return `${formattedHour}:${minute
                            .toString()
                            .padStart(2, "0")} ${period}`;
                    };

                    return {
                        id: job.id,
                        job_date: job.job_date,
                        start_time: formatTime(job.start_time),
                        end_time: formatTime(job.end_time),
                        technicians: job.technicians || [],
                        job_scope: jobScope,
                        client: job.client.name,
                    };
                });

                const today = new Date().toISOString().split('T')[0];

                const pastDueJobOrders = transformedResults
                    .filter(order => order.job_date < today) // Filter orders before today
                    .sort((a, b) => new Date(b.job_date) - new Date(a.job_date)); // Sort by descending date

                const upcomingJobOrders = transformedResults
                    .filter(order => order.job_date >= today)
                    .sort((a, b) => {
                        const dateDiff = new Date(a.job_date) - new Date(b.job_date);
                        if (dateDiff === 0) {
                            return a.start_time.localeCompare(b.start_time); // Sort by start time if same date
                        }
                        return dateDiff;
                    });


                const openJobOrdersCount = {
                    total: results.length,
                    pastDue: pastDueJobOrders.length,
                    upcoming: upcomingJobOrders.length
                };

                setOpenJobOrdersCount(openJobOrdersCount)
                setPastDueJobOrders(pastDueJobOrders)
                setUpcomingJobOrders(upcomingJobOrders)


                setError(null); // Clear any previous errors
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false); // Ensure loading is stopped after fetch
            }
        };
        fetchJobOrders();
    }, []);




    return (

        <BaseWrapper
            titleIcon={<PendingActionsIcon />}
            title="Operations"
            subTitle="View Open Job Orders Report"
            subTitleRoute={"/view-open-job-orders-report"}
            // createButtonLabel="Create Service Report"
            // onClickCreateButton={() => navigate("/create-service-report")}
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            isLoading={isLoading}
        >

            <Box sx={{ p: 2 }}>
                <Grid container spacing={2} >
                    {/* Top Row Metrics */}
                    <Grid item xs={12} container spacing={1} >
                        <Grid item xs={12} md={4}>
                            <Card
                                sx={{
                                    backgroundColor: '#1976D2',
                                    color: '#FFF',
                                    borderRadius: 2,
                                    textAlign: 'center',
                                }}
                            >
                                <CardContent>
                                    <Typography variant="h6">Total Open Job Orders</Typography>
                                    <Typography variant="h3">{openJobOrdersCount.total}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card
                                sx={{
                                    backgroundColor: '#D32F2F',
                                    color: '#FFF',
                                    borderRadius: 2,
                                    textAlign: 'center',
                                }}
                            >
                                <CardContent>
                                    <Typography variant="h6">Past Due</Typography>
                                    <Typography variant="h3">{openJobOrdersCount.pastDue}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card
                                sx={{
                                    backgroundColor: '#4CAF50',
                                    color: '#FFF',
                                    borderRadius: 2,
                                    textAlign: 'center',
                                }}
                            >
                                <CardContent>
                                    <Typography variant="h6">Upcoming</Typography>
                                    <Typography variant="h3">{openJobOrdersCount.upcoming}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>


                    <Grid item xs={6} md={6}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}>
                            Past Due
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                                backgroundColor: "#f7f7f7",
                                            }}
                                        >
                                            Job Date
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                                backgroundColor: "#f7f7f7",
                                            }}
                                        >
                                            Start Time
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                                backgroundColor: "#f7f7f7",
                                            }}
                                        >
                                            End Time
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                                backgroundColor: "#f7f7f7",
                                            }}
                                        >
                                            Client
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                                backgroundColor: "#f7f7f7",
                                                width: "35%", // Job Scope Column Width
                                            }}
                                        >
                                            Job Scope
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pastDueJobOrders.map((job, index) => (
                                        <TableRow key={index}>
                                            <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                {job.job_date ? (
                                                    <a
                                                        href={`/view-job-order/${job.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: "#1976d2",
                                                            textDecoration: "none",
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {job.job_date}
                                                    </a>
                                                ) : (
                                                    ""
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                {job.start_time || ""}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                {job.end_time || ""}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                {job.client || ""}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.75rem",
                                                    padding: "6px",
                                                    width: "25%", // Body Cell Width Matching the Header
                                                }}
                                            >
                                                {job.job_scope.length > 0 ? (
                                                    <ol
                                                        style={{
                                                            margin: "0",
                                                            padding: "0",
                                                            listStylePosition: "inside",
                                                        }}
                                                    >
                                                        {job.job_scope.map((scope, i) => {
                                                            const formattedCompetency =
                                                                scope.competency.toLowerCase() === "qpms"
                                                                    ? "QPMS"
                                                                    : scope.competency
                                                                        .toLowerCase()
                                                                        .replace(/_/g, " ")
                                                                        .replace(/\b\w/g, (char) =>
                                                                            char.toUpperCase()
                                                                        );
                                                            return (
                                                                <li key={i}>
                                                                    <b>{formattedCompetency}</b>
                                                                    {scope.specialization.length > 0
                                                                        ? `: ${scope.specialization.join(", ")}`
                                                                        : ""}
                                                                </li>
                                                            );
                                                        })}
                                                    </ol>
                                                ) : (
                                                    ""
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                    <Grid item xs={6} md={6}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}>
                            Upcoming
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                                backgroundColor: "#f7f7f7",
                                            }}
                                        >
                                            Job Date
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                                backgroundColor: "#f7f7f7",
                                            }}
                                        >
                                            Start Time
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                                backgroundColor: "#f7f7f7",
                                            }}
                                        >
                                            End Time
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                                backgroundColor: "#f7f7f7",
                                            }}
                                        >
                                            Client
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: "bold",
                                                padding: "6px",
                                                backgroundColor: "#f7f7f7",
                                                width: "35%", // Job Scope Column Width
                                            }}
                                        >
                                            Job Scope
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {upcomingJobOrders.map((job, index) => (
                                        <TableRow key={index}>
                                            <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                {job.job_date ? (
                                                    <a
                                                        href={`/view-job-order/${job.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: "#1976d2",
                                                            textDecoration: "none",
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {job.job_date}
                                                    </a>
                                                ) : (
                                                    ""
                                                )}
                                            </TableCell>


                                            <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                {job.start_time || ""}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                {job.end_time || ""}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                                                {job.client || ""}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: "0.75rem",
                                                    padding: "6px",
                                                    width: "25%", // Body Cell Width Matching the Header
                                                }}
                                            >
                                                {job.job_scope.length > 0 ? (
                                                    <ol
                                                        style={{
                                                            margin: "0",
                                                            padding: "0",
                                                            listStylePosition: "inside",
                                                        }}
                                                    >
                                                        {job.job_scope.map((scope, i) => {
                                                            const formattedCompetency =
                                                                scope.competency.toLowerCase() === "qpms"
                                                                    ? "QPMS"
                                                                    : scope.competency
                                                                        .toLowerCase()
                                                                        .replace(/_/g, " ")
                                                                        .replace(/\b\w/g, (char) =>
                                                                            char.toUpperCase()
                                                                        );
                                                            return (
                                                                <li key={i}>
                                                                    <b>{formattedCompetency}</b>
                                                                    {scope.specialization.length > 0
                                                                        ? `: ${scope.specialization.join(", ")}`
                                                                        : ""}
                                                                </li>
                                                            );
                                                        })}
                                                    </ol>
                                                ) : (
                                                    ""
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                </Grid>
            </Box>



        </BaseWrapper>
        // <ThemeProvider theme={theme}>
        //     <Box sx={{ padding: "40px 60px" }}>
        //         <Card
        //             variant="outlined"
        //             style={{
        //                 marginTop: "40px",
        //                 marginBottom: "40px",
        //                 borderRadius: 0,
        //             }}
        //         >
        //             <CardHeader
        //                 title={
        //                     <Box
        //                         display="flex"
        //                         alignItems="center"
        //                         justifyContent="space-between"
        //                     >
        //                         <Box display="flex" alignItems="center">
        //                             <PendingActionsIcon
        //                                 sx={{
        //                                     fontSize: "40px",
        //                                     marginRight: "8px",
        //                                 }}
        //                             />
        //                             <Typography variant="h6">Operations</Typography>
        //                         </Box>
        //                         <Breadcrumbs
        //                             aria-label="breadcrumb"
        //                             separator={<NavigateNextIcon fontSize="small" />}
        //                             sx={{ color: "white" }}
        //                         >
        //                             <Typography sx={{ color: "#ffffff99" }}>
        //                                 Operations
        //                             </Typography>
        //                             <Typography sx={{ color: "#ffffff", textDecoration: "none" }}>
        //                                 View Operation Completion Report
        //                             </Typography>
        //                         </Breadcrumbs>
        //                     </Box>
        //                 }
        //                 sx={{
        //                     backgroundColor: "#1e5d45",
        //                     color: "white",
        //                     padding: "16px",
        //                 }}
        //             />
        //             <CardContent sx={{ padding: "20px" }}>
        //                 <Card variant="outlined" sx={{ padding: "20px" }}>
        //                     <CardHeader
        //                         title={
        //                             <Typography variant="h5" sx={{ fontWeight: "bold" }}>
        //                                 Open Job Orders Report
        //                             </Typography>
        //                         }
        //                         sx={{ marginBottom: 2 }}
        //                     />

        //                     <Box sx={{ p: 2 }}>
        //                         <Grid container spacing={2} >
        //                             {/* Top Row Metrics */}
        //                             <Grid item xs={12} container spacing={1} >
        //                                 <Grid item xs={12} md={4}>
        //                                     <Card
        //                                         sx={{
        //                                             backgroundColor: '#1976D2',
        //                                             color: '#FFF',
        //                                             borderRadius: 2,
        //                                             textAlign: 'center',
        //                                         }}
        //                                     >
        //                                         <CardContent>
        //                                             <Typography variant="h6">Total Open Job Orders</Typography>
        //                                             <Typography variant="h3">{openJobOrdersCount.total}</Typography>
        //                                         </CardContent>
        //                                     </Card>
        //                                 </Grid>
        //                                 <Grid item xs={12} md={4}>
        //                                     <Card
        //                                         sx={{
        //                                             backgroundColor: '#D32F2F',
        //                                             color: '#FFF',
        //                                             borderRadius: 2,
        //                                             textAlign: 'center',
        //                                         }}
        //                                     >
        //                                         <CardContent>
        //                                             <Typography variant="h6">Past Due</Typography>
        //                                             <Typography variant="h3">{openJobOrdersCount.pastDue}</Typography>
        //                                         </CardContent>
        //                                     </Card>
        //                                 </Grid>
        //                                 <Grid item xs={12} md={4}>
        //                                     <Card
        //                                         sx={{
        //                                             backgroundColor: '#4CAF50',
        //                                             color: '#FFF',
        //                                             borderRadius: 2,
        //                                             textAlign: 'center',
        //                                         }}
        //                                     >
        //                                         <CardContent>
        //                                             <Typography variant="h6">Upcoming</Typography>
        //                                             <Typography variant="h3">{openJobOrdersCount.upcoming}</Typography>
        //                                         </CardContent>
        //                                     </Card>
        //                                 </Grid>
        //                             </Grid>


        //                             <Grid item xs={6} md={6}>
        //                                 <Typography variant="subtitle1" sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}>
        //                                     Past Due
        //                                 </Typography>
        //                                 <TableContainer>
        //                                     <Table size="small">
        //                                         <TableHead>
        //                                             <TableRow>
        //                                                 <TableCell
        //                                                     sx={{
        //                                                         fontSize: "0.8rem",
        //                                                         fontWeight: "bold",
        //                                                         padding: "6px",
        //                                                         backgroundColor: "#f7f7f7",
        //                                                     }}
        //                                                 >
        //                                                     Job Date
        //                                                 </TableCell>
        //                                                 <TableCell
        //                                                     sx={{
        //                                                         fontSize: "0.8rem",
        //                                                         fontWeight: "bold",
        //                                                         padding: "6px",
        //                                                         backgroundColor: "#f7f7f7",
        //                                                     }}
        //                                                 >
        //                                                     Start Time
        //                                                 </TableCell>
        //                                                 <TableCell
        //                                                     sx={{
        //                                                         fontSize: "0.8rem",
        //                                                         fontWeight: "bold",
        //                                                         padding: "6px",
        //                                                         backgroundColor: "#f7f7f7",
        //                                                     }}
        //                                                 >
        //                                                     End Time
        //                                                 </TableCell>
        //                                                 <TableCell
        //                                                     sx={{
        //                                                         fontSize: "0.8rem",
        //                                                         fontWeight: "bold",
        //                                                         padding: "6px",
        //                                                         backgroundColor: "#f7f7f7",
        //                                                     }}
        //                                                 >
        //                                                     Client
        //                                                 </TableCell>
        //                                                 <TableCell
        //                                                     sx={{
        //                                                         fontSize: "0.8rem",
        //                                                         fontWeight: "bold",
        //                                                         padding: "6px",
        //                                                         backgroundColor: "#f7f7f7",
        //                                                         width: "35%", // Job Scope Column Width
        //                                                     }}
        //                                                 >
        //                                                     Job Scope
        //                                                 </TableCell>
        //                                             </TableRow>
        //                                         </TableHead>
        //                                         <TableBody>
        //                                             {pastDueJobOrders.map((job, index) => (
        //                                                 <TableRow key={index}>
        //                                                     <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                         {job.job_date ? (
        //                                                             <a
        //                                                                 href={`/view-job-order/${job.id}`}
        //                                                                 target="_blank"
        //                                                                 rel="noopener noreferrer"
        //                                                                 style={{
        //                                                                     color: "#1976d2",
        //                                                                     textDecoration: "none",
        //                                                                     fontWeight: "bold",
        //                                                                 }}
        //                                                             >
        //                                                                 {job.job_date}
        //                                                             </a>
        //                                                         ) : (
        //                                                             ""
        //                                                         )}
        //                                                     </TableCell>
        //                                                     <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                         {job.start_time || ""}
        //                                                     </TableCell>
        //                                                     <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                         {job.end_time || ""}
        //                                                     </TableCell>
        //                                                     <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                         {job.client || ""}
        //                                                     </TableCell>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.75rem",
        //                                                             padding: "6px",
        //                                                             width: "25%", // Body Cell Width Matching the Header
        //                                                         }}
        //                                                     >
        //                                                         {job.job_scope.length > 0 ? (
        //                                                             <ol
        //                                                                 style={{
        //                                                                     margin: "0",
        //                                                                     padding: "0",
        //                                                                     listStylePosition: "inside",
        //                                                                 }}
        //                                                             >
        //                                                                 {job.job_scope.map((scope, i) => {
        //                                                                     const formattedCompetency =
        //                                                                         scope.competency.toLowerCase() === "qpms"
        //                                                                             ? "QPMS"
        //                                                                             : scope.competency
        //                                                                                 .toLowerCase()
        //                                                                                 .replace(/_/g, " ")
        //                                                                                 .replace(/\b\w/g, (char) =>
        //                                                                                     char.toUpperCase()
        //                                                                                 );
        //                                                                     return (
        //                                                                         <li key={i}>
        //                                                                             <b>{formattedCompetency}</b>
        //                                                                             {scope.specialization.length > 0
        //                                                                                 ? `: ${scope.specialization.join(", ")}`
        //                                                                                 : ""}
        //                                                                         </li>
        //                                                                     );
        //                                                                 })}
        //                                                             </ol>
        //                                                         ) : (
        //                                                             ""
        //                                                         )}
        //                                                     </TableCell>
        //                                                 </TableRow>
        //                                             ))}
        //                                         </TableBody>
        //                                     </Table>
        //                                 </TableContainer>
        //                             </Grid>

        //                             <Grid item xs={6} md={6}>
        //                                 <Typography variant="subtitle1" sx={{ fontWeight: "bold", marginBottom: "8px", fontSize: "1.2rem" }}>
        //                                     Upcoming
        //                                 </Typography>
        //                                 <TableContainer>
        //                                     <Table size="small">
        //                                         <TableHead>
        //                                             <TableRow>
        //                                                 <TableCell
        //                                                     sx={{
        //                                                         fontSize: "0.8rem",
        //                                                         fontWeight: "bold",
        //                                                         padding: "6px",
        //                                                         backgroundColor: "#f7f7f7",
        //                                                     }}
        //                                                 >
        //                                                     Job Date
        //                                                 </TableCell>
        //                                                 <TableCell
        //                                                     sx={{
        //                                                         fontSize: "0.8rem",
        //                                                         fontWeight: "bold",
        //                                                         padding: "6px",
        //                                                         backgroundColor: "#f7f7f7",
        //                                                     }}
        //                                                 >
        //                                                     Start Time
        //                                                 </TableCell>
        //                                                 <TableCell
        //                                                     sx={{
        //                                                         fontSize: "0.8rem",
        //                                                         fontWeight: "bold",
        //                                                         padding: "6px",
        //                                                         backgroundColor: "#f7f7f7",
        //                                                     }}
        //                                                 >
        //                                                     End Time
        //                                                 </TableCell>
        //                                                 <TableCell
        //                                                     sx={{
        //                                                         fontSize: "0.8rem",
        //                                                         fontWeight: "bold",
        //                                                         padding: "6px",
        //                                                         backgroundColor: "#f7f7f7",
        //                                                     }}
        //                                                 >
        //                                                     Client
        //                                                 </TableCell>
        //                                                 <TableCell
        //                                                     sx={{
        //                                                         fontSize: "0.8rem",
        //                                                         fontWeight: "bold",
        //                                                         padding: "6px",
        //                                                         backgroundColor: "#f7f7f7",
        //                                                         width: "35%", // Job Scope Column Width
        //                                                     }}
        //                                                 >
        //                                                     Job Scope
        //                                                 </TableCell>
        //                                             </TableRow>
        //                                         </TableHead>
        //                                         <TableBody>
        //                                             {upcomingJobOrders.map((job, index) => (
        //                                                 <TableRow key={index}>
        //                                                     <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                         {job.job_date ? (
        //                                                             <a
        //                                                                 href={`/view-job-order/${job.id}`}
        //                                                                 target="_blank"
        //                                                                 rel="noopener noreferrer"
        //                                                                 style={{
        //                                                                     color: "#1976d2",
        //                                                                     textDecoration: "none",
        //                                                                     fontWeight: "bold",
        //                                                                 }}
        //                                                             >
        //                                                                 {job.job_date}
        //                                                             </a>
        //                                                         ) : (
        //                                                             ""
        //                                                         )}
        //                                                     </TableCell>


        //                                                     <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                         {job.start_time || ""}
        //                                                     </TableCell>
        //                                                     <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                         {job.end_time || ""}
        //                                                     </TableCell>
        //                                                     <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
        //                                                         {job.client || ""}
        //                                                     </TableCell>
        //                                                     <TableCell
        //                                                         sx={{
        //                                                             fontSize: "0.75rem",
        //                                                             padding: "6px",
        //                                                             width: "25%", // Body Cell Width Matching the Header
        //                                                         }}
        //                                                     >
        //                                                         {job.job_scope.length > 0 ? (
        //                                                             <ol
        //                                                                 style={{
        //                                                                     margin: "0",
        //                                                                     padding: "0",
        //                                                                     listStylePosition: "inside",
        //                                                                 }}
        //                                                             >
        //                                                                 {job.job_scope.map((scope, i) => {
        //                                                                     const formattedCompetency =
        //                                                                         scope.competency.toLowerCase() === "qpms"
        //                                                                             ? "QPMS"
        //                                                                             : scope.competency
        //                                                                                 .toLowerCase()
        //                                                                                 .replace(/_/g, " ")
        //                                                                                 .replace(/\b\w/g, (char) =>
        //                                                                                     char.toUpperCase()
        //                                                                                 );
        //                                                                     return (
        //                                                                         <li key={i}>
        //                                                                             <b>{formattedCompetency}</b>
        //                                                                             {scope.specialization.length > 0
        //                                                                                 ? `: ${scope.specialization.join(", ")}`
        //                                                                                 : ""}
        //                                                                         </li>
        //                                                                     );
        //                                                                 })}
        //                                                             </ol>
        //                                                         ) : (
        //                                                             ""
        //                                                         )}
        //                                                     </TableCell>
        //                                                 </TableRow>
        //                                             ))}
        //                                         </TableBody>
        //                                     </Table>
        //                                 </TableContainer>
        //                             </Grid>

        //                         </Grid>
        //                     </Box>











        //                 </Card>
        //             </CardContent>
        //         </Card>
        //     </Box>
        // </ThemeProvider >
    )

}

export default ViewOpenJobOrders;